/********************************/
/*          About vuex          */
/********************************/
// The MIT License (MIT)
//
// Copyright (c) 2015-present Evan You
//
// Permission is hereby granted, free of charge, to any person obtaining a copy
// of this software and associated documentation files (the "Software"), to deal
// in the Software without restriction, including without limitation the rights
// to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
// copies of the Software, and to permit persons to whom the Software is
// furnished to do so, subject to the following conditions:
//
// The above copyright notice and this permission notice shall be included in
// all copies or substantial portions of the Software.
//
// THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
// IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
// FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
// AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
// LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
// OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN
// THE SOFTWARE.

import { mapState, mapActions } from 'vuex';
import individualDiagnose from '../../../../share/util/individualDiagnose';
import config from '../../../../share/config';
import api from '../../../../common/api';

/** 各フェーズ定義 */
const PHASE = {
  START: 'start',
  WAIT: 'wait',
  SUCCESS: 'success',
  FAILED: 'failed',
};

export default {
  name: 'realWorldDataVSOC',
  data() {
    return {
      phase: PHASE.START,
      isLoading: false,
      modal_real_world_data_vsoc: false
    };
  },

  computed: {
    ...mapState({
      netAppId: state => state.netAppConnector.pairingAppId
    }),
    /**
     * ダイアログのヘッダー色
     */
    headerColor() {
      switch (this.phase) {
        case PHASE.FAILED:
          return 'danger';
        case PHASE.SUCCESS:
          return 'success';
        case PHASE.WAIT:
        case PHASE.START:
        default:
          return 'secondary';
      }
    }
  },

  methods: {
    ...mapActions('auth', ['logout', 'resetSession']),

    /** 初期化処理 */
    init() {
      this.modal_real_world_data_vsoc = true;
    },

    /**
     * その他のエラー
     */
    showUnexpectedError() {
      // 結果確認（失敗）画面に遷移
      this.phase = PHASE.FAILED;
    },

    /**
    * API 通信で共通バリデーションでエラーが発生した際のコールバック
    * @param {object} result handler.validate で返ってきたバリデーション結果
    */
    validateErrorResult(result) {
      individualDiagnose.apiErrorDefaultAction(
        this,
        result,
        // ログアウト処理のコールバック（Vuex の Action を呼びだしている点に注意）
        (self) => { self; this.logout(this); },
        // セッションリセット処理のコールバック（Vuex の Action を呼びだしている点に注意）
        (self) => { self; this.resetSession(this); },
        // その他エラー発生のコールバック
        // result を使って、任意の処理をすることも可能
        (result) => { result; this.showUnexpectedError(); },
      );
    },

    /** 実行処理 */
    async executeProcess() {
      try {
        this.phase = PHASE.WAIT;
        this.isLoading = true;
        this.phase = PHASE.WAIT;
        // バックエンドAPI POST,GET
        const response = await individualDiagnose.pollingApi(
          this,
          // POST API 呼び出しのコールバック
          async () => {
            return await api.postCall(
              config.DIAG,
              '/netApps/' + this.netAppId + '/realWorldData/vsoc'
            );
          },
          // GET API 呼び出しのコールバック
          async (postApiResponse) => {
            return await api.getCall(
              config.DIAG,
              '/netApps/' + this.netAppId + '/realWorldData/requests/' + postApiResponse.data.request_id
            );
          },
          // エラー発生のコールバック
          (errorType) => { errorType; this.showUnexpectedError(); },
          // POST API レスポンスチェックのコールバック
          (postApiResponse) => {
            // リクエストID が正常に返ってきたら成功とする
            return postApiResponse && postApiResponse.data && postApiResponse.data.request_id ? '' : config.INDIVIDUAL_API_CALL_ERROR_POST_INVALID_RESPONSE;
          },
          // GET API レスポンスチェックのコールバック
          (getApiResponse) => {
            let result = config.INDIVIDUAL_POLLING_RESULT_ERROR;
            if (getApiResponse && getApiResponse.data && getApiResponse.data.status) {
              switch (getApiResponse.data.status) {
                case config.STATUS_REPRO_COMPLETED:
                  // ポーリング終了を返す
                  result = config.INDIVIDUAL_POLLING_RESULT_FINISH;
                  break;
                case config.STATUS_REPRO_IN_PROGRESS:
                  // ポーリング続行を返す
                  result = config.INDIVIDUAL_POLLING_RESULT_CONTINUE;
                  break;
                case config.STATUS_REPRO_FAILED:
                default:
                  // それ以外はエラー発生を返す
                  break;
              }
            }
            return result;
          },
          // POST API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバックを定義する（validateErrorResult の中は下記参照）
          (result) => { this.validateErrorResult(result); },
          // GET API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバックを定義する（validateErrorResult の中は下記参照）
          (result) => { this.validateErrorResult(result); },
          // リトライ回数を指定：1080回（10秒間隔で180分）
          process.env.VUE_APP_REAL_WORLD_VSOC_RETRY_COUNT,
          // リトライ間隔を指定：10秒
          process.env.VUE_APP_REAL_WORLD_RETRY_INTERVAL,
        );

        if (response) {
          // 正常コールバック
          switch (response.data.status) {
            case config.STATUS_REPRO_COMPLETED:
              // 正常終了：結果表示画面へ遷移し成功した旨を表示
              this.phase = PHASE.SUCCESS;
              break;
            case config.STATUS_REPRO_FAILED:
            default:
              // 異常終了：結果表示画面へ遷移し失敗した旨を表示
              this.phase = PHASE.FAILED;
              return;
          }
        }
      } finally {
        this.isLoading = false;
      }
    },

    /** Wizardを閉じる */
    async closeWizard() {
      this.$emit('close-wizard');
      this.modal_real_world_data_vsoc = false;
    }
  },
  mounted() {
    this.init();
  }
};